import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import customerService from '../../../utils/customerService';

const DeleteContactModal = ({ isOpen, onClose, contact, onConfirm }) => {
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Load deletion impact when modal opens
  useEffect(() => {
    if (isOpen && contact?.id) {
      loadDeletionImpact();
    }
  }, [isOpen, contact?.id]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDeletionImpact(null);
      setConfirmText('');
      setDeleting(false);
    }
  }, [isOpen]);

  const loadDeletionImpact = async () => {
    try {
      setLoading(true);
      const impact = await customerService.getContactDeletionImpact(contact.id);
      setDeletionImpact(impact);
    } catch (error) {
      console.error('Error loading deletion impact:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (confirmText !== 'DELETE' || !deletionImpact?.canDelete) return;
    
    try {
      setDeleting(true);
      await onConfirm(contact.id);
      onClose();
    } catch (error) {
      console.error('Error deleting contact:', error);
    } finally {
      setDeleting(false);
    }
  };

  const getCustomerDisplayName = (customer) => {
    if (customer.customer_type === 'organization' && customer.organization_name) {
      return customer.organization_name;
    }
    return customer.name;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !contact) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-error-50 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error-100 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-error" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-error">Delete Contact</h3>
              <p className="text-sm text-error-700">This action cannot be undone</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-text-muted hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-text-secondary">Analyzing deletion impact...</p>
            </div>
          ) : deletionImpact ? (
            <>
              {/* Contact Information */}
              <div className="bg-card-hover rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3 flex items-center">
                  <Icon name="User" size={16} className="mr-2" />
                  Contact to be deleted
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Name:</span>
                    <span className="font-medium text-foreground">{getCustomerDisplayName(deletionImpact.contact)}</span>
                  </div>
                  {deletionImpact.contact.customer_type === 'organization' && (
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted">Contact Person:</span>
                      <span className="text-foreground">{deletionImpact.contact.name}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Email:</span>
                    <span className="text-foreground">{deletionImpact.contact.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Phone:</span>
                    <span className="text-foreground">{deletionImpact.contact.phone || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Pipeline Stage:</span>
                    <span className="text-foreground capitalize">{deletionImpact.contact.pipeline_stage || 'Unknown'}</span>
                  </div>
                </div>
              </div>

              {/* Impact Warning */}
              <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                <h4 className="font-medium text-error mb-3 flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-2" />
                  Deletion Impact
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-error-700">Reviews from this contact:</span>
                    <span className="font-bold text-error">{deletionImpact.reviewCount}</span>
                  </div>
                  <div className="mt-3 p-3 bg-error-100 rounded border-l-4 border-error">
                    <p className="text-error-800 text-xs">
                      <Icon name="Info" size={12} className="inline mr-1" />
                      {deletionImpact.canDelete
                        ? 'This contact has no submitted reviews and can be safely deleted.'
                        : 'Deleting a contact is permanent and cannot be undone.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Alternative Action */}
              <div className={`rounded-lg p-4 ${!deletionImpact.canDelete ? 'bg-amber-50 border border-amber-300' : 'bg-blue-50 border border-blue-200'}`}>
                <h4 className={`font-medium mb-2 flex items-center ${!deletionImpact.canDelete ? 'text-amber-800' : 'text-blue-800'}`}>
                  <Icon name="Archive" size={16} className="mr-2" />
                  {!deletionImpact.canDelete ? 'Archiving is Required' : 'Consider Archiving Instead'}
                </h4>
                <p className={`text-sm mb-3 ${!deletionImpact.canDelete ? 'text-amber-700' : 'text-blue-700'}`}>
                  {deletionImpact.canDelete
                    ? 'Archiving preserves the contact and their history while removing them from active views. This is recommended for compliance and data retention.'
                    : 'To protect your business records, contacts with submitted reviews cannot be deleted. Archiving will preserve their data while hiding them from your active client list.'}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className={!deletionImpact.canDelete 
                    ? 'border-amber-400 text-amber-800 hover:bg-amber-100' 
                    : 'border-blue-300 text-blue-700 hover:bg-blue-100'}
                  onClick={() => {
                    // This should trigger an archive action on the parent page
                    console.log('Archive button clicked');
                    onClose(); // Close modal, parent should handle archive
                  }}
                >
                  <Icon name="Archive" size={14} className="mr-2" />
                  Archive Contact
                </Button>
              </div>

              {/* Confirmation Input */}
              <div className="space-y-3">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${!deletionImpact.canDelete ? 'text-text-muted' : 'text-foreground'}`}>
                    Type <code className={`px-1 rounded ${!deletionImpact.canDelete ? 'bg-gray-200 text-gray-500' : 'bg-error-100 text-error-800'}`}>DELETE</code> to confirm permanent deletion:
                  </label>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type DELETE to confirm"
                    disabled={!deletionImpact.canDelete}
                    className={`${confirmText === 'DELETE' && deletionImpact.canDelete ? 'border-error-300 focus:border-error-500' : ''} ${!deletionImpact.canDelete ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                </div>
                {!deletionImpact.canDelete && (
                  <p className="text-xs text-amber-600">
                    Deletion is disabled because this contact has submitted reviews. Please use the "Archive" option above.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Icon name="AlertTriangle" size={48} className="text-error mx-auto mb-4" />
              <p className="text-error">Failed to load deletion impact information</p>
              <Button variant="outline" onClick={loadDeletionImpact} className="mt-4">
                Retry
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-card-hover flex-shrink-0">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={confirmText !== 'DELETE' || deleting || loading || !deletionImpact?.canDelete}
            loading={deleting}
            title={!deletionImpact?.canDelete ? 'This contact cannot be deleted because they have reviews' : 'Delete Permanently'}
          >
            {deleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteContactModal; 